import React from "react";
import { scopedAnchor, usePracticeScope } from "../../../../onboarding/practice/practiceScope";
import { primaryText, textBlue, borderPrimary } from "../../../../../styling/stylingHelper";
import { eur } from "../../../../orderhistory/utils";

interface SummaryCardsProps {
    profitMargin: number;
    unitsSold: number;
    menuPrice: number;
    totalCost: number;
    costPercentage: number | null;
    isCostBad: boolean;
    onEditMenuPrice: () => void;
}

const SummaryCards: React.FC<SummaryCardsProps> = ({
    profitMargin, unitsSold, menuPrice, totalCost,
    costPercentage, isCostBad, onEditMenuPrice
}) => {
    const practiceScope = usePracticeScope();
    return (
        <>
            {/* Top row: Profit Margin + Units Sold */}
            <div className="grid grid-cols-2 gap-4">
                <div className={`rounded-xl border ${borderPrimary} bg-background dark:bg-background-dark p-5`}>
                    <p className={`text-[18px] leading-6 font-semibold ${primaryText}`}>Profit Margin</p>
                    <p className={`mt-3 text-[24px] sm:text-[28px] leading-8 sm:leading-[38px] font-bold ${textBlue}`}>
                        €{profitMargin.toFixed(2)}
                    </p>
                </div>
                <div className={`rounded-xl border ${borderPrimary} bg-background dark:bg-background-dark p-5`}>
                    <p className={`text-[18px] leading-6 font-semibold ${primaryText}`}>Units Sold</p>
                    <p className={`mt-3 text-[24px] sm:text-[28px] leading-8 sm:leading-[38px] font-bold ${primaryText}`}>
                        {unitsSold}
                    </p>
                </div>
            </div>

            {/* Bottom row: Menu Price + Total Cost */}
            <div className="grid grid-cols-2 gap-4 mt-4">
                <div className={`rounded-xl border ${borderPrimary} bg-background dark:bg-background-dark p-5 relative`}>
                    <div className="flex items-start justify-between">
                        <p className={`text-[18px] leading-6 font-semibold ${primaryText}`}>Menu Price</p>
                        <button
                            type="button"
                            onClick={onEditMenuPrice}
                            className="p-1 hover:opacity-70 transition-opacity"
                            title="Edit menu price"
                        >
                            <img src="/assets/edit.svg" alt="edit" />
                        </button>
                    </div>
                    <p className={`mt-3 text-[24px] sm:text-[28px] leading-8 sm:leading-[38px] font-bold ${primaryText}`}>
                        €{menuPrice.toFixed(2)}
                    </p>
                </div>
                <div className={`rounded-xl border ${borderPrimary} bg-background dark:bg-background-dark p-5`} data-onboarding={scopedAnchor(practiceScope, "cost-detail-totalcost")}>
                    <p className={`text-[18px] leading-6 font-semibold ${primaryText}`}>Total Cost</p>
                    <p className={`mt-3 text-[24px] sm:text-[28px] leading-8 sm:leading-[38px] font-bold ${primaryText}`}>
                        {eur(totalCost)}
                    </p>
                </div>
            </div>

            {/* Cost percentage bar */}
            {costPercentage !== null && (
                <div className="space-y-3 my-5">
                    <div className="flex items-center justify-between">
                        <p className="text-[16px] leading-5 font-semibold text-[#0C111D] dark:text-textPrimary-dark">Cost percentage</p>
                        <span
                            className={`inline-flex items-center rounded-lg px-2 h-7 text-[14px] leading-[18px] font-semibold ${isCostBad ? "bg-[#FFF4E6] text-[#eb8d00]" : "bg-[#E7FBF5] text-[#166534]"}`}
                        >
                            {costPercentage.toFixed(1)}%
                        </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-[#E8E8E9] dark:bg-neutral-700 overflow-hidden">
                        <div
                            className={`h-full rounded-full ${isCostBad ? "bg-[#eb8d00]" : "bg-[#0E8965]"}`}
                            style={{ width: `${Math.min(100, Math.max(0, costPercentage))}%` }}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default SummaryCards;
